import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {MimeTypeNamespace} from '@namespaces/mime-type.namespace';
import {UploadedFile} from '@interfaces/file.interface';
import {HttpClient} from '@angular/common/http';
import {take} from 'rxjs/operators';
import {untilDestroyed} from 'ngx-take-until-destroy';

enum MimeType {
    pdf,
    image,
    tiff,
}

/**
 * Компонент предпросмотра файла по его URL.
 * Умеет отображать изображения и PDF файлы (средствами браузера).
 */

@Component({
    selector: 'app-file-preview',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: 'file-preview.component.html',
    styleUrls: ['file-preview.component.scss'],
})
export class FilePreviewComponent implements OnInit, OnDestroy {
    @Input() uploadedFile: UploadedFile;

    /**
     * iframe с файлом для печати
     */
    @ViewChild('iframe') iframeRef: ElementRef;
    /**
     * Доступные типы данных.
     * Сохраняем enum в виде свойства, чтобы иметь к нему доступ из View
     * @type {MimeType}
     */
    public mimeType = MimeType;

    /**
     * Тип данных ресурса по ссылке this.url
     */
    public resourceType: MimeType;

    public sanitizedUrl: SafeResourceUrl;

    /**
     * Возможен ли предпросмотр файла в браузере (и как следствие печать файла)
     */
    public isPreviewable = false;

    public error = false;

    private objectUrl: string;

    constructor(
        private sanitizer: DomSanitizer,
        private cd: ChangeDetectorRef,
        private http: HttpClient,
    ) {

    }

    ngOnInit() {
        this.checkResourceType();

        this.http.get(this.uploadedFile.file_url, {
            responseType: 'blob'
        }).pipe(
            take(1),
            untilDestroyed(this),
        ).subscribe((file) => {
            if (!file) {
                this.error = true;
                this.cd.detectChanges();
                return;
            }
            this.objectUrl = window.URL.createObjectURL(file);
            this.sanitizedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.objectUrl);
            this.cd.detectChanges();
        }, () => {
            this.error = true;
            this.cd.detectChanges();
        });
    }

    ngOnDestroy(): void {
        if (this.objectUrl) {
            window.URL.revokeObjectURL(this.objectUrl);
        }
    }

    printFile() {
        if (!this.isPreviewable) {
            return;
        }

        try {
            const iframe: HTMLIFrameElement = this.iframeRef.nativeElement;
            const document = iframe.contentDocument;

            if (document && document.head) {
                const style = document.createElement('style');
                style.innerHTML = `
                img {
                    max-width: 100%;
                }
            `;

                document.head.append(style);
                iframe.contentWindow.print();
                /**
                 * По хорошему нужно удалять style после печати, но этого не отследить, так как в том же Safari модалка запроса
                 * разрешения на печать тоже воспринимается как print, но после нее еще нельзя удалять.
                 * Пусть накапливается, пока не критично.
                 */
            } else {
                iframe.contentWindow.print();
            }
        } catch (e) {
            window.open(this.uploadedFile.file_url);
        }

        return false;
    }

    /**
     * Проверить тип ресурса по его URL.
     */
    private checkResourceType() {
        if (!this.uploadedFile) {
            this.resourceType = null;
            this.isPreviewable = false;
            return;
        }

        const fileType = this.uploadedFile.file_mime_type;

        /**
         * Для предпросмотра возможны картинки и PDF
         */
        this.isPreviewable = MimeTypeNamespace.isFilePreviewableInBrowser(fileType);

        if (fileType === MimeTypeNamespace.MimeEnums.pdf) {
            this.resourceType = MimeType.pdf;
            return;
        }

        if (fileType === MimeTypeNamespace.MimeEnums.tiff) {
            this.resourceType = MimeType.tiff;
            return;
        }

        if (MimeTypeNamespace.getImageTypes().includes(fileType)) {
            this.resourceType = MimeType.image;
            return;
        }

        this.resourceType = null;
    }
}
